import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Calendar,
  MessageSquare,
  FileText,
  ShieldCheck,
  ShieldAlert,
  Star,
  Clock
} from 'lucide-react';
import { Notification, NotificationType } from '../types';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  compact?: boolean;
}

const getNotificationIcon = (type: NotificationType) => {
  const iconClass = "h-5 w-5";

  switch (type) {
    case 'BOOKING_APPROVED':
      return <CheckCircle className={`${iconClass} text-green-600`} />;
    case 'BOOKING_COMPLETED':
      return <CheckCircle className={`${iconClass} text-blue-600`} />;
    case 'BOOKING_CANCELLED':
      return <XCircle className={`${iconClass} text-red-600`} />;
    case 'BOOKING_REJECTED':
      return <XCircle className={`${iconClass} text-orange-600`} />;
    case 'BOOKING_CREATED':
      return <Calendar className={`${iconClass} text-blue-600`} />;
    case 'MESSAGE_RECEIVED':
      return <MessageSquare className={`${iconClass} text-purple-600`} />;
    case 'POST_RESPONSE':
      return <FileText className={`${iconClass} text-indigo-600`} />;
    case 'PROFILE_VERIFIED':
      return <ShieldCheck className={`${iconClass} text-green-600`} />;
    case 'PROFILE_BLOCKED':
      return <ShieldAlert className={`${iconClass} text-red-600`} />;
    case 'REVIEW_RECEIVED':
      return <Star className={`${iconClass} text-yellow-600`} />;
    default:
      return <Clock className={`${iconClass} text-gray-600`} />;
  }
};

const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
  }

  return date.toLocaleDateString();
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete,
  compact = false,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!notification.isRead) {
      onMarkAsRead(notification._id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification._id);
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkAsRead(notification._id);
  };

  return (
    <div
      onClick={handleClick}
      className={`p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer ${
        !notification.isRead ? 'bg-blue-50' : 'bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-1">
          {getNotificationIcon(notification.type)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <h4 className={`text-sm font-semibold ${!notification.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
                {notification.title}
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                {notification.message}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {getRelativeTime(notification.createdAt)}
              </p>
            </div>

            {!compact && (
              <div className="flex items-center gap-2">
                {!notification.isRead && (
                  <button
                    onClick={handleMarkAsRead}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Mark as read
                  </button>
                )}
                <button
                  onClick={handleDelete}
                  className="text-xs text-red-600 hover:text-red-800 font-medium"
                >
                  Delete
                </button>
              </div>
            )}
          </div>

          {!notification.isRead && (
            <div className="absolute top-4 right-4 w-2 h-2 bg-blue-600 rounded-full"></div>
          )}
        </div>
      </div>
    </div>
  );
};
